import {useCallback, useEffect, useId, useRef, useState} from 'react'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {clsx} from 'clsx'

import styles from './BackgroundGrid.module.css'
import {
  areGridDimensionsEqual,
  getEvictedTrailPixelIds,
  getGridDimensions,
  getTrailGridPosition,
  shouldCreateTrailPixel,
  type GridDimensions,
  type GridPosition,
} from './background-grid-helpers'
import {BACKGROUND_COLOR, COLORS, DARK_BACKGROUND_COLOR, DARK_GRID_LINE_COLOR, GRID_LINE_COLOR} from './config'

const CELL_SIZE = 1
const TRAIL_EXIT_DURATION_MS = 100

type TrailPixel = {
  id: number
  x: number
  y: number
  color: string
  exiting: boolean
}

type TrailPixelTimeouts = {
  exit: number
  positionIndex: number
  removal: number
}

type CornerColors = {
  left: string
  leftInner: string
  right: string
  rightInner: string
}

type BackgroundGridState = GridDimensions & {
  cornerColors: CornerColors | null
}

export type BackgroundGridProps = {
  className?: string
  hasCornerDots?: boolean
  hasCursorTrain?: boolean
  theme?: 'light' | 'dark'
}

function randomColor() {
  return COLORS[Math.floor(Math.random() * COLORS.length)] ?? COLORS[0]
}

function randomColorExcept(excludedColor: string) {
  const availableColors = COLORS.filter(color => color !== excludedColor)
  return availableColors[Math.floor(Math.random() * availableColors.length)] ?? COLORS[0]
}

function randomCornerColors(): CornerColors {
  const left = randomColor()
  const right = randomColor()

  return {
    left,
    leftInner: randomColorExcept(left),
    right,
    rightInner: randomColorExcept(right),
  }
}

export function BackgroundGrid({
  className,
  hasCornerDots = false,
  hasCursorTrain = true,
  theme = 'light',
}: BackgroundGridProps) {
  const backgroundColor = theme === 'dark' ? DARK_BACKGROUND_COLOR : BACKGROUND_COLOR
  const gridLineColor = theme === 'dark' ? DARK_GRID_LINE_COLOR : GRID_LINE_COLOR
  const backgroundGridId = useId().replace(/:/g, '')
  const gradientId = `background-grid-gradient-${backgroundGridId}`
  const patternId = `background-grid-pattern-${backgroundGridId}`
  const wrapperRef = useRef<HTMLDivElement>(null)
  const nextTrailPixelIdRef = useRef(0)
  const trailPixelIdsRef = useRef<number[]>([])
  const activeTrailPositionsRef = useRef<Set<number>>(new Set())
  const lastTrailPositionRef = useRef<GridPosition | null>(null)
  const trailTimeoutsRef = useRef<Map<number, TrailPixelTimeouts>>(new Map())
  const [trailPixels, setTrailPixels] = useState<TrailPixel[]>([])
  const [gridState, setGridState] = useState<BackgroundGridState>(() => ({
    ...getGridDimensions(0, 0),
    cornerColors: null,
  }))
  const {cellSizePx, cornerColors, height: gridHeight, width: gridWidth} = gridState
  const prefersReducedMotion = usePrefersReducedMotion()
  const canShowCursorTrain = hasCursorTrain && !prefersReducedMotion
  const trailContext = `${gridWidth}:${gridHeight}:${cellSizePx}:${hasCursorTrain}:${prefersReducedMotion}`
  const [previousTrailContext, setPreviousTrailContext] = useState(trailContext)

  if (previousTrailContext !== trailContext) {
    setPreviousTrailContext(trailContext)
    setTrailPixels([])
  }

  const clearTrailPixelTimeout = useCallback((id: number) => {
    const timeouts = trailTimeoutsRef.current.get(id)
    if (timeouts !== undefined) {
      window.clearTimeout(timeouts.exit)
      window.clearTimeout(timeouts.removal)
      activeTrailPositionsRef.current.delete(timeouts.positionIndex)
      trailTimeoutsRef.current.delete(id)
    }
  }, [])

  const clearTrail = useCallback(() => {
    for (const id of [...trailTimeoutsRef.current.keys()]) {
      clearTrailPixelTimeout(id)
    }
    trailPixelIdsRef.current = []
    activeTrailPositionsRef.current.clear()
    lastTrailPositionRef.current = null
  }, [clearTrailPixelTimeout])

  const removeTrailPixel = useCallback(
    (id: number) => {
      clearTrailPixelTimeout(id)
      trailPixelIdsRef.current = trailPixelIdsRef.current.filter(pixelId => pixelId !== id)
      setTrailPixels(currentPixels => currentPixels.filter(pixel => pixel.id !== id))
    },
    [clearTrailPixelTimeout],
  )

  const addTrailPixel = useCallback(
    (x: number, y: number) => {
      const positionIndex = y * gridWidth + x
      if (activeTrailPositionsRef.current.has(positionIndex)) {
        return
      }

      const id = nextTrailPixelIdRef.current++
      const exitDelay = 100 + 100 * Math.random()
      activeTrailPositionsRef.current.add(positionIndex)
      const exitTimeout = window.setTimeout(() => {
        setTrailPixels(currentPixels =>
          currentPixels.map(pixel => (pixel.id === id ? {...pixel, exiting: true} : pixel)),
        )
      }, exitDelay)
      const removalTimeout = window.setTimeout(() => {
        removeTrailPixel(id)
      }, exitDelay + TRAIL_EXIT_DURATION_MS)

      trailTimeoutsRef.current.set(id, {
        exit: exitTimeout,
        positionIndex,
        removal: removalTimeout,
      })
      trailPixelIdsRef.current.push(id)
      const evictedIds = getEvictedTrailPixelIds(trailPixelIdsRef.current)

      if (evictedIds.length > 0) {
        trailPixelIdsRef.current = trailPixelIdsRef.current.slice(evictedIds.length)
        for (const evictedId of evictedIds) {
          clearTrailPixelTimeout(evictedId)
        }
      }

      setTrailPixels(currentPixels => {
        const retainedPixels = evictedIds.length
          ? currentPixels.filter(pixel => !evictedIds.includes(pixel.id))
          : currentPixels

        return [
          ...retainedPixels,
          {
            id,
            x,
            y,
            color: randomColor(),
            exiting: false,
          },
        ]
      })
    },
    [clearTrailPixelTimeout, gridWidth, removeTrailPixel],
  )

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!canShowCursorTrain) {
        return
      }

      const rect = event.currentTarget.getBoundingClientRect()
      const position = getTrailGridPosition(event.clientX, event.clientY, rect, gridWidth, gridHeight, cellSizePx)

      if (position === null) {
        return
      }

      if (
        !shouldCreateTrailPixel(
          lastTrailPositionRef.current,
          position,
          prefersReducedMotion,
          activeTrailPositionsRef.current.has(position.y * gridWidth + position.x),
        )
      ) {
        lastTrailPositionRef.current = position
        return
      }

      lastTrailPositionRef.current = position
      addTrailPixel(position.x, position.y)
    },
    [addTrailPixel, canShowCursorTrain, cellSizePx, gridHeight, gridWidth, prefersReducedMotion],
  )

  const handlePointerLeave = useCallback(() => {
    lastTrailPositionRef.current = null
  }, [])

  useEffect(() => {
    const wrapper = wrapperRef.current
    const parent = wrapper?.parentElement

    if (!parent) {
      return
    }

    const updateGridDimensions = ({height, width}: Pick<DOMRect, 'height' | 'width'>) => {
      const nextDimensions = getGridDimensions(width, height)
      setGridState(currentState => {
        const nextCornerColors = hasCornerDots
          ? (currentState.cornerColors ?? randomCornerColors())
          : currentState.cornerColors

        if (areGridDimensionsEqual(currentState, nextDimensions) && nextCornerColors === currentState.cornerColors) {
          return currentState
        }

        return {
          ...nextDimensions,
          cornerColors: nextCornerColors,
        }
      })
    }

    updateGridDimensions(parent.getBoundingClientRect())

    const resizeObserver = new ResizeObserver(([entry]) => {
      if (entry) updateGridDimensions(entry.contentRect)
    })
    resizeObserver.observe(parent)

    return () => {
      resizeObserver.disconnect()
    }
  }, [hasCornerDots])

  useEffect(() => {
    clearTrail()
  }, [clearTrail, trailContext])

  useEffect(() => {
    const trailPixelIds = trailPixelIdsRef.current
    const activeTrailPositions = activeTrailPositionsRef.current
    const trailTimeouts = trailTimeoutsRef.current

    return () => {
      for (const {exit, removal} of trailTimeouts.values()) {
        window.clearTimeout(exit)
        window.clearTimeout(removal)
      }
      trailPixelIds.length = 0
      activeTrailPositions.clear()
      trailTimeouts.clear()
    }
  }, [])

  return (
    <div
      className={clsx(styles.Wrapper, className)}
      onPointerLeave={handlePointerLeave}
      onPointerMove={canShowCursorTrain ? handlePointerMove : undefined}
      ref={wrapperRef}
    >
      {gridWidth > 0 && gridHeight > 0 && (
        <svg
          aria-hidden="true"
          className={styles.Svg}
          height={gridHeight * cellSizePx}
          preserveAspectRatio="none"
          viewBox={`0 0 ${gridWidth} ${gridHeight}`}
          width={gridWidth * cellSizePx}
        >
          <defs>
            <pattern id={patternId} height="1" patternUnits="userSpaceOnUse" width="1">
              <path className={styles.GridLine} d="M 1 0 H 0 V 1" stroke={gridLineColor} />
            </pattern>
            <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor={backgroundColor} stopOpacity="1" />
              <stop offset="0.5" stopColor={backgroundColor} stopOpacity="0" />
              <stop offset="1" stopColor={backgroundColor} stopOpacity="0" />
            </linearGradient>
          </defs>

          <rect fill={backgroundColor} height={gridHeight} width={gridWidth} />
          <rect fill={`url(#${patternId})`} height={gridHeight} width={gridWidth} />
          <path className={styles.GridLine} d={`M ${gridWidth} 0 V ${gridHeight} H 0`} stroke={gridLineColor} />
          <rect fill={`url(#${gradientId})`} height={gridHeight} width={gridWidth} />
          {hasCornerDots && cornerColors && gridWidth > 3 && gridHeight > 3 && (
            <>
              <rect fill={cornerColors.left} height={CELL_SIZE} width={CELL_SIZE} x={CELL_SIZE * 2} y={CELL_SIZE * 2} />
              <rect
                fill={cornerColors.right}
                height={CELL_SIZE}
                width={CELL_SIZE}
                x={gridWidth - CELL_SIZE * 3}
                y={CELL_SIZE * 2}
              />
              <rect
                fill={cornerColors.leftInner}
                height={CELL_SIZE}
                width={CELL_SIZE}
                x={CELL_SIZE * 3}
                y={CELL_SIZE * 3}
              />
              <rect
                fill={cornerColors.rightInner}
                height={CELL_SIZE}
                width={CELL_SIZE}
                x={gridWidth - CELL_SIZE * 4}
                y={CELL_SIZE * 3}
              />
            </>
          )}
          {trailPixels.map(pixel => (
            <rect
              className={pixel.exiting ? `${styles.TrailPixel} ${styles.TrailPixelExiting}` : styles.TrailPixel}
              fill={pixel.color}
              height={CELL_SIZE}
              key={pixel.id}
              width={CELL_SIZE}
              x={pixel.x}
              y={pixel.y}
            />
          ))}
        </svg>
      )}
    </div>
  )
}
